import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Home, MessageCircle, Target, Sparkles, Trophy, ShoppingBag, User, MoreHorizontal, Users, Settings, ChevronRight, ChevronsLeft, BarChart3, LogOut, Clock, CheckCircle2, Info, X, CreditCard, Star, LayoutGrid, Rocket, Compass, BookOpen, Medal, Layers, Sun, Moon, Phone, GraduationCap, Sigma, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePreferences } from '@/context/PreferencesContext';
import { useUserProfile } from '@/context/UserProfileContext';
import { useDiscovery } from '@/context/DiscoveryContext';
import { useCompliance } from '@/context/ComplianceContext';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { getPortalType, getUserData, getAuthToken, isAuthenticated } from '@/utils/auth';
import { getAnonymousUserPreferences } from '@/utils/anonymousAuth';
import { apiUrl, mediaUrl } from '@/api/config';
import { shopAPI } from '@/api/shop';

import api from '@/services/api';
import axios from 'axios';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationList from './Notifications/NotificationList';
import { motion, AnimatePresence } from 'framer-motion';
import { preloadStudentRoute } from '@/routes/studentRoutePreloaders';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const menuItems = [{ id: 'learn', label: 'LEARN', icon: Home, color: 'text-orange-500', type: 'regular' },
{ id: 'chat', label: 'CHAT', icon: MessageCircle, color: 'text-green-400', type: 'regular' },
{
  id: 'quests',
  label: 'QUESTS',
  icon: Target,
  color: 'text-orange-400',
  type: 'regular',
},
{
  id: 'tuitions',
  label: 'TUITIONS',
  icon: GraduationCap,
  color: 'text-indigo-400',
  type: 'regular',
},

{
  id: 'curiosity',
  label: 'CURIOSITY CENTRE',
  icon: Sparkles,
  color: 'text-purple-400',
  type: 'expandable',
  children: [
    { id: 'curiosity-chapters', label: 'CHAPTER TOPICS', icon: Layers, color: 'text-purple-400' },
    { id: 'curiosity-topics', label: 'ADVANCED TOPICS', icon: Rocket, color: 'text-purple-400' },
  ]
},
{
  id: 'achievements',
  label: 'ACHIEVEMENTS',
  icon: Trophy,
  color: 'text-yellow-500',
  type: 'expandable',
  children: [
    { id: 'students-analytics', label: 'MY ANALYTICS', icon: BarChart3, color: 'text-emerald-400' },
    { id: 'leaderboard', label: 'LEADERBOARD', icon: Medal, color: 'text-yellow-400' },
    { id: 'founders', label: 'STUDENT FOUNDERS', icon: Star, color: 'text-yellow-500' },
    { id: 'shop', label: 'SHOP', icon: ShoppingBag, color: 'text-red-400' },
  ]
},
{
  id: 'more',
  label: 'MORE',
  icon: MoreHorizontal,
  color: 'text-gray-500',
  type: 'expandable',
  children: [
    { id: 'profile', label: 'PROFILE', icon: User, color: 'text-gray-400' },
    { id: 'affiliate-earnings', label: 'EARNINGS', icon: DollarSign, color: 'text-emerald-400' },
    { id: 'subscriptions', label: 'UPGRADE PLAN', icon: CreditCard, color: 'text-blue-400' },
    { id: 'settings', label: 'SETTINGS', icon: Settings, color: 'text-gray-400' },
    { id: 'quick-guide', label: 'USER GUIDE', icon: BookOpen, color: 'text-green-400' },
    { id: 'about', label: 'ABOUT', icon: Info, color: 'text-green-400' },
  ]
},
];

export default function Sidebar({ activeSection, setActiveSection, onNavigate = () => { }, showCloseButton = false, onClose = () => { }, onLogout = () => { }, onUnlockFullAccess = () => { }, onCallClick, isCallDisabled, iconOnly = false }) {
  const { show_subscriptions, hide_tuition } = useCompliance();
  const { selectedChapterId } = useSubjectChapter();
  const {
    profile,
    subscription,
    inventory,
    refreshProfile
  } = useUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [expandedItem, setExpandedItem] = useState(null);
  const portal = getPortalType(location.pathname || '');
  const [isHovered, setIsHovered] = useState(false);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [equippedItems, setEquippedItems] = useState({
    CHARACTER: null,
    AVATAR: null,
    FRAME: null,
  });
  const { theme, toggleTheme, config } = usePreferences();
  const isDark = theme === 'dark';
  const { t } = useTranslation(['nav', 'common']);

  const getNavLabel = (id, fallback) => {
    const keyMap = {
      'learn': 'learn',
      'chat': 'chat',
      'quests': 'quests',
      'tuitions': 'tuitions',
      'curiosity': 'curiosityCentre',
      'curiosity-chapters': 'chapterTopics',
      'curiosity-topics': 'advancedTopics',
      'achievements': 'achievements',
      'students-analytics': 'myAnalytics',
      'leaderboard': 'leaderboard',
      'founders': 'studentFounders',
      'shop': 'shop',
      'more': 'more',
      'profile': 'profile',
      'affiliate-earnings': 'earnings',
      'subscriptions': 'upgradePlan',
      'settings': 'settings',
      'quick-guide': 'userGuide',
      'about': 'about',
    };
    const key = keyMap[id];
    return key ? t(`nav:${key}`, fallback) : fallback;
  };

  // Check if user is authenticated
  const token = getAuthToken(portal);
  const userData = getUserData(portal);
  const isUserAuthenticated = isAuthenticated(portal);
  const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
  const isGuest = !isUserAuthenticated && (isActuallyGuest || !!getAnonymousUserPreferences());

  const isNative = Capacitor.isNativePlatform();
  const { unreadCount, refreshUnreadCount } = useNotifications(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);

  // Filter menu items based on authentication
  // Show achievements (leaderboard/shop) for both authenticated and guest users
  // Hide profile/settings for guests
  const visibleMenuItems = menuItems.map(item => {
    if (item.id === 'tuitions') {
      if (isGuest || hide_tuition || config?.budlee_tuitions_enabled !== true) {
        return null;
      }
    }
    if (item.id === 'achievements') {
      // Always show achievements for authenticated users and guests
      // Filter out analytics for guests
      return {
        ...item,
        children: item.children.filter(child => {
          if (isGuest && child.id === 'students-analytics') return false;
          if (!show_subscriptions && child.id === 'shop') return false;
          return true;
        })
      };
    }
    if (item.id === 'more') {
      // Filter out profile and settings for guest users
      // Also hide UPGRADE PLAN for native apps
      return {
        ...item,
        children: item.children.filter(child => {
          if (child.id === 'affiliate-earnings') {
            if (!show_subscriptions) return false;
            return !isGuest && Boolean(profile?.is_affiliator || userData?.is_affiliator);
          }
          if (isGuest && ['profile', 'students-analytics', 'subscriptions'].includes(child.id)) return false;
          if (!show_subscriptions && child.id === 'subscriptions') return false;
          if (profile?.institute && child.id === 'subscriptions') return false;
          return true;
        })
      };
    }
    // Show other items only for authenticated users
    if (isUserAuthenticated) {
      return item;
    }
    // For guests, show learn, chat, quests, curiosity, achievements
    if (['learn', 'chat', 'quests', 'curiosity', 'achievements'].includes(item.id)) {
      return item;
    }
    return null;
  }).filter(Boolean);

  // Auto-expand "MORE" menu if on profile, settings
  // Auto-expand "QUESTS" menu if on quests page
  // Auto-expand "CHAT" menu if on chat page
  useEffect(() => {
    if (
      location.pathname.includes('/profile') ||
      location.pathname.includes('/subscriptions') ||
      location.pathname.includes('/settings') ||
      location.pathname.includes('/about')) {
      setExpandedItem('more');
    } else if (location.pathname.includes('/quests')) {
      setExpandedItem('quests');
    } else if (location.pathname.includes('/leaderboard') || location.pathname.includes('/shop') || location.pathname.includes('/founders')) {
      setExpandedItem('achievements');
    } else if (location.pathname.includes('/analytics')) {
      setExpandedItem('achievements');
    } else if (location.pathname.includes('/curiosity')) {
      setExpandedItem('curiosity');
    }
  }, [location.pathname]);

  // Map user profile from context to local UI format
  useEffect(() => {
    if (profile) {
      const photoField = profile.profile_photo || profile.profile_pic_url || profile.photo || profile.avatar;
      const photoUrl = photoField ? mediaUrl(photoField) : null;
      const isCustomCurriculum = Boolean(profile.is_custom_curriculum || profile.board?.is_custom);
      const gradeName = profile.student?.grade?.grade_name || profile.grade?.grade_name || null;
      const gradeNumber = profile.student?.grade?.grade_number || profile.grade?.grade_number || null;

      setUserProfile({
        photo: photoUrl,
        fullName: profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username || 'User',
        username: profile.username?.includes('__') ? profile.username.split('__')[1] : (profile.username || ''),
        isCustomCurriculum: isCustomCurriculum,
        syllabus: isCustomCurriculum ? null : (profile.board?.board_code || profile.board?.board_name || profile.syllabus || null),
        grade: isCustomCurriculum ? null : gradeName,
        gradeNumber: isCustomCurriculum ? null : gradeNumber,
        planName: subscription?.plan_name || (subscription?.status === 'FREE' || !subscription?.status ? 'Free' : null),
        isFounder: profile.is_founder || false,
      });
    }
  }, [profile, subscription]);

  // Map equipped items from context-provided inventory
  useEffect(() => {
    if (inventory && Array.isArray(inventory)) {
      const equipped = {
        CHARACTER: inventory.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'CHARACTER') || null,
        AVATAR: inventory.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'AVATAR') || null,
        FRAME: inventory.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'FRAME') || null,
      };
      setEquippedItems(equipped);
    }
  }, [inventory]);

  const handleParentClick = (itemId) => {
    // Toggle accordion - only one open at a time
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  const handleChildClick = (childId, parentId) => {
    preloadStudentRoute(childId)?.catch(() => { });

    const portal = getPortalType(location.pathname || '');
    const userData = getUserData(portal);
    const userType = userData?.user_type || portal;

    setActiveSection(childId);

    // Navigate to specific routes for children
    if (childId === 'quick-guide') {
      window.dispatchEvent(new CustomEvent('showDetailedUserGuide', { detail: { force: true } }));
      if (onNavigate) onNavigate();
      return;
    }

    if (childId === 'profile') {
      if (userType === 'student') {
        navigate('/student/profile');
      } else if (userType === 'teacher') {
        navigate('/teacher/profile');
      }
    } else if (childId === 'affiliate-earnings') {
      if (userType === 'student') {
        navigate('/student/earnings');
      }
    } else if (childId === 'students-analytics') {
      if (userType === 'student') {
        navigate('/student/analytics');
      }
    } else if (childId === 'leaderboard') {
      if (userType === 'student') {
        navigate('/student/achievements/leaderboard');
      }
    } else if (childId === 'shop') {
      if (userType === 'student') {
        navigate('/student/achievements/shop');
      }
    } else if (childId === 'founders') {
      if (userType === 'student') {
        navigate('/student/achievements/founders');
      }
    } else if (childId === 'subscriptions') {
      const userFullType = userData?.user_type || portal;
      if (userFullType === 'teacher') {
        navigate('/teacher/profile');
      }
    } else if (childId === 'settings') {
      if (userType === 'student') {
        navigate('/student/settings');
      }
    } else if (childId === 'about') {
      if (userType === 'student') {
        navigate('/student/about');
      }
    } else if (childId === 'new-quest' || childId === 'ongoing' || childId === 'completed' || childId === 'drafts') {
      // Quest submenu items - navigate to quests page with tab parameter
      if (userType === 'student') {
        navigate(`/student/quests?tab=${childId}`);
      }
    } else if (childId === 'curiosity-chapters') {
      if (userType === 'student') {
        navigate('/student/curiosity');
      }
    } else if (childId === 'curiosity-topics') {
      if (userType === 'student') {
        navigate('/student/curiosity/topics');
      }
    }
    // Collapse sidebar back to icon only for student portal
    if (portal === 'student' && !showCloseButton) {
      setIsHovered(false);
      setIsManuallyExpanded(false);
    }
    // Keep parent expanded when child is clicked
    try { onNavigate(); } catch { }
  };

  const handleItemClick = (itemId) => {
    preloadStudentRoute(itemId)?.catch(() => { });

    const portal = getPortalType(location.pathname || '');
    const userData = getUserData(portal);
    const userType = userData?.user_type || portal;

    // Special handling for routes that need navigation
    if (itemId === 'chat') {
      // Only students can access chat
      if (userType === 'student') {
        navigate('/student/chat');
      } else {
        setActiveSection('learn');
      }
    } else if (itemId === 'quests') {
      // Only students can access quests
      if (userType === 'student') {
        navigate('/student/quests?tab=new-quest');
      } else {
        setActiveSection('learn');
      }
    } else if (itemId === 'tuitions') {
      if (userType === 'student') {
        navigate('/student/tuitions');
      } else {
        setActiveSection('learn');
      }

    } else if (itemId === 'profile') {
      if (userType === 'student') {
        navigate('/student/profile');
      } else if (userType === 'teacher') {
        navigate('/teacher/profile');
      }
    } else if (itemId === 'learn') {
      // For teachers/admins, go to teach page
      if (userType === 'teacher') {
        navigate('/teacher/teach');
      } else {
        // Students use Learn page
        navigate('/student/learn');
      }
    } else {
      // For other sections, use the setActiveSection callback
      // which will be handled by Dashboard
      setActiveSection(itemId);
    }
    // Collapse sidebar back to icon only for student portal
    if (portal === 'student' && !showCloseButton) {
      setIsHovered(false);
      setIsManuallyExpanded(false);
    }
    // Close overlay when navigating or selecting a non-expandable item
    try {
      const item = menuItems.find(m => m.id === itemId);
      if (!item || item.type !== 'expandable') onNavigate();
    } catch { }
  };

  const isChildActive = (item) => {
    if (item.type === 'expandable' && item.children) {
      return item.children.some(child => child.id === activeSection);
    }
    return false;
  };

  // Check if a quest child item is active based on URL query parameter
  // Use useEffect to ensure searchParams updates trigger re-renders
  const isQuestChildActive = (childId) => {
    // Only check on the main quests page (/student/quests), not sub-pages
    if (location.pathname === '/student/quests') {
      // Get tab from searchParams hook (reactive to URL changes)
      const tabFromUrl = searchParams.get('tab');
      // Default to 'new-quest' if no tab parameter is present (matching QuestsPage behavior)
      const activeTab = tabFromUrl || 'new-quest';
      // Strict comparison to ensure exact match
      return activeTab === childId;
    }
    return false;
  };

  const renderProfileAvatar = (size = 'normal') => {
    const isSmall = size === 'compact';
    const userType = userData?.user_type || portal;
    const useProfilePicAsAvatar = (userType === 'teacher' || userType === 'admin') || (userData?.use_profile_pic_as_avatar || false);
    const imageToShow = (userType === 'student' && !useProfilePicAsAvatar && equippedItems.AVATAR?.shop_item?.image_url_display)
      ? equippedItems.AVATAR.shop_item.image_url_display
      : (userProfile?.photo || null);
    const hasImage = !!imageToShow;

    return (
      <div className={cn(
        'rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center overflow-visible flex-shrink-0 relative',
        isSmall ? 'w-10 h-10' : 'w-10 h-10 sm:w-12 sm:h-12'
      )}>
        {/* Avatar/Profile Photo - Base Layer */}
        <div className="absolute inset-0 w-full h-full rounded-full overflow-hidden z-0">
          {hasImage ? (
            <img
              src={imageToShow}
              alt={userProfile?.fullName || 'User'}
              className="w-full h-full object-cover"
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : null}
          <div className={cn(
            "absolute inset-0 w-full h-full items-center justify-center",
            hasImage ? "hidden" : "flex"
          )}>
            <User className={cn("text-white", isSmall ? "w-5 h-5" : "w-6 h-6")} />
          </div>
        </div>

        {/* Profile Frame Overlay */}
        {equippedItems.FRAME?.shop_item?.image_url_display && (
          <div className={cn(
            "absolute rounded-full overflow-visible pointer-events-none z-10",
            isSmall ? "-inset-2.5" : "-inset-3"
          )}>
            <img
              src={equippedItems.FRAME.shop_item.image_url_display}
              alt="Frame"
              className="w-full h-full object-contain"
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
        )}

        {/* Founder Badge */}
        {userProfile?.isFounder && (
          <div
            className={cn(
              "absolute -top-1 -right-1 z-20 bg-yellow-500 rounded-full border border-white dark:border-gray-800 shadow-md flex items-center justify-center",
              isSmall ? "p-0.5" : "p-1"
            )}
            title="Student Founder"
          >
            <Star className={cn("text-white fill-current", isSmall ? "w-2.5 h-2.5" : "w-2.5 h-2.5 sm:w-3 sm:h-3")} />
          </div>
        )}
      </div>
    );
  };

  const isStudentPortal = portal === 'student';
  const expanded = showCloseButton || (isStudentPortal ? (isHovered || isManuallyExpanded) : !iconOnly);
  const compact = !expanded;

  return (
    <div
      className={cn(
        'h-[100dvh] bg-[hsl(var(--sidebar-bg))] sm:bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--card-border))] flex flex-col transition-all duration-300 ease-out z-40 pb-safe select-none',
        showCloseButton && 'pt-safe-top pl-safe-left',
        compact ? 'w-16' : 'w-64'
      )}
      data-icon-only={compact ? 'true' : 'false'}
      onMouseEnter={!showCloseButton ? () => setIsHovered(true) : undefined}
      onMouseLeave={!showCloseButton ? () => {
        setIsHovered(false);
        setIsManuallyExpanded(false);
      } : undefined}
    >
      {/* Logo + Brand Name */}
      <div className={cn(
        'flex flex-col gap-1 relative border-b border-[hsl(var(--card-border))]/55 transition-all duration-300',
        expanded ? 'items-start px-5 pt-6 pb-6' : 'items-center justify-center p-3 pt-5 pb-5'
      )}>
        <div className={cn('flex items-center gap-2.5 w-full min-w-0', expanded ? 'justify-between' : 'justify-center')}>
          {expanded ? (
            <div className="flex-1 flex flex-col justify-center min-w-0">
              <div className="w-fit max-w-[180px] sm:max-w-[200px] flex flex-col">
                <div
                  className="relative inline-flex items-center cursor-pointer select-none self-start"
                  onClick={() => setIsNotifOpen(!isNotifOpen)}
                  title="Notifications"
                >
                  <img
                    src="/logo_tagline_black.png"
                    alt="Budlee AI"
                    className="dark:hidden h-8 sm:h-9 md:h-9.5 object-contain w-auto max-w-[180px] sm:max-w-[200px]"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  <img
                    src="/logo_tagline_white.png"
                    alt="Budlee AI"
                    className="hidden dark:block h-8 sm:h-9 md:h-9.5 object-contain w-auto max-w-[180px] sm:max-w-[200px]"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  {unreadCount > 0 && (
                    <div className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full border-2 border-[hsl(var(--sidebar-bg))] flex items-center justify-center shadow-md animate-bounce">
                      <span className="text-[10px] font-black text-white leading-none">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    </div>
                  )}
                </div>
                {profile?.institute?.name && (
                  <div className="flex justify-end w-full mt-1">
                    <span className="text-[10px] sm:text-[11px] font-bold text-muted-foreground select-none truncate flex items-center">
                      <span className="italic font-medium text-[hsl(var(--teal-vivid))] mr-1">For</span>
                      <span className="text-foreground tracking-wide font-black uppercase text-[9px] sm:text-[10px] bg-[hsl(var(--teal-vivid))]/10 border border-[hsl(var(--teal-vivid))]/30 px-1.5 py-0.5 rounded-md truncate max-w-[140px] sm:max-w-[160px]">{profile.institute.name}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              className="relative cursor-pointer select-none"
              onClick={() => {
                if (unreadCount > 0) {
                  setIsNotifOpen(!isNotifOpen);
                } else if (compact) {
                  setIsManuallyExpanded(true);
                  localStorage.setItem('budlee_sidebar_expanded', 'true');
                } else {
                  setIsNotifOpen(!isNotifOpen);
                }
              }}
              title={unreadCount > 0 ? "Notifications" : (compact ? "Expand menu" : "Notifications")}
            >
              <img
                src="/logo.png"
                alt="Budlee AI Logo"
                className="h-9 w-9 sm:h-10 sm:w-10 object-contain rounded-lg"
                onContextMenu={(e) => e.preventDefault()}
              />
              {unreadCount > 0 && (
                <div className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full border-2 border-[hsl(var(--sidebar-bg))] flex items-center justify-center shadow-md animate-bounce">
                  <span className="text-[10px] font-black text-white leading-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Desktop Collapse Button when Expanded */}
          {expanded && !showCloseButton && !isStudentPortal && (
            <button
              onClick={() => {
                setIsManuallyExpanded(false);
                localStorage.setItem('budlee_sidebar_expanded', 'false');
              }}
              className="p-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors shrink-0"
              title="Collapse sidebar to icon only"
              aria-label="Collapse sidebar"
            >
              <ChevronsLeft className="w-4 h-4 rtl:rotate-180" />
            </button>
          )}
        </div>

        {/* Notification Portal for Desktop */}
        <AnimatePresence>
          {isNotifOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[150] bg-black/5"
                onClick={() => setIsNotifOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, x: -20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -20, scale: 0.95 }}
                className="absolute top-20 left-6 z-[160]"
              >
                <NotificationList
                  onClose={() => setIsNotifOpen(false)}
                  onRefreshCount={refreshUnreadCount}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>
        {/* Close Button for Mobile */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className="p-1 rounded-full bg-white/90 hover:bg-white shadow z-50 lg:hidden"
            title="Close sidebar"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
        )}
      </div>

      {/* Menu Items */}
      <nav className={cn('flex-1 space-y-1 overflow-y-auto', expanded ? 'px-3' : 'px-2')}>
        {visibleMenuItems.map((item) => {
          const Icon = item.icon;
          const isExpanded = expandedItem === item.id;
          const isActive = (
            (activeSection === item.id && !(item.id === 'learn' && location.pathname.includes('/tuitions'))) ||
            isChildActive(item) ||
            (item.id === 'chat' && location.pathname.includes('/chat')) ||
            (item.id === 'tuitions' && location.pathname.includes('/tuitions'))
          );
          const localizedItemLabel = getNavLabel(item.id, item.label);

          return (
            <div key={item.id}>
              {/* Parent Item */}
              <button
                onClick={() => {
                  if (compact) {
                    // When collapsed, clicking on sidebar menu expands to full text and icons
                    setIsManuallyExpanded(true);
                    localStorage.setItem('budlee_sidebar_expanded', 'true');
                    if (item.type === 'expandable') {
                      setExpandedItem(item.id);
                    } else {
                      handleItemClick(item.id);
                    }
                  } else if (item.type === 'expandable') {
                    handleParentClick(item.id);
                  } else {
                    handleItemClick(item.id);
                  }
                }}
                onPointerEnter={() => preloadStudentRoute(item.type === 'expandable' ? item.children?.[0]?.id : item.id)?.catch(() => { })}
                onFocus={() => preloadStudentRoute(item.type === 'expandable' ? item.children?.[0]?.id : item.id)?.catch(() => { })}
                className={cn(
                  'w-full flex items-center rounded-xl font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                  expanded ? 'gap-4 px-4 py-3.5' : 'p-3 justify-center',
                  isActive
                    ? 'bg-[hsl(var(--sidebar-hover))] text-foreground shadow-lg'
                    : 'text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-foreground'
                )}
                title={localizedItemLabel}
              >
                {/* Neon gradient border for active state */}
                {isActive && (
                  <div className="absolute inset-0 rounded-xl p-[2px] bg-gradient-to-r from-[hsl(var(--teal-vivid))] via-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] animate-pulse-glow">
                    <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-xl" />
                  </div>
                )}

                {/* Content */}
                <div className={cn('relative z-10 flex items-center w-full', expanded ? 'gap-4' : 'justify-center gap-0')}>
                  <Icon className={cn('w-6 h-6 shrink-0', item.color)} />
                  {expanded && <span className="flex-1 text-start text-xs sm:text-sm">{localizedItemLabel}</span>}

                  {/* Chevron for expandable items */}
                  {expanded && item.type === 'expandable' && (
                    <ChevronRight
                      className={cn(
                        'w-4 h-4 transition-transform duration-300 rtl:rotate-180',
                        isExpanded && 'rotate-90 rtl:rotate-90'
                      )}
                    />
                  )}
                </div>

                {/* Hover glow effect */}
                {!isActive && (
                  <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-[hsl(var(--teal-vivid))]/10 to-[hsl(var(--green-bright))]/10 rounded-xl" />
                )}
              </button>

              {/* Children Items (Dropdown) */}
              {expanded && item.type === 'expandable' && item.children && (
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
                  )}
                >
                  <div className="space-y-1 ps-4 pe-0 py-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      // For quests children, check URL query parameter; for others, use activeSection
                      const isChildActiveState = item.id === 'quests'
                        ? isQuestChildActive(child.id)
                        : activeSection === child.id;
                      const localizedChildLabel = getNavLabel(child.id, child.label);

                      return (
                        <button
                          key={child.id}
                          onClick={() => handleChildClick(child.id, item.id)}
                          onPointerEnter={() => preloadStudentRoute(child.id)?.catch(() => { })}
                          onFocus={() => preloadStudentRoute(child.id)?.catch(() => { })}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                            isChildActiveState
                              ? 'bg-[hsl(var(--sidebar-hover))] text-foreground shadow-md'
                              : 'text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))]/70 hover:text-foreground'
                          )}
                        >
                          {/* Neon gradient border for active child */}
                          {isChildActiveState && (
                            <div className="absolute inset-0 rounded-lg p-[2px] bg-gradient-to-r from-[hsl(var(--teal-vivid))] via-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] animate-pulse-glow">
                              <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-lg" />
                            </div>
                          )}

                          {/* Child Content */}
                          <div className="relative z-10 flex items-center gap-2 sm:gap-3 w-full">
                            <ChildIcon className={cn('w-4 h-4 sm:w-5 sm:h-5', child.color)} />
                            <span className="text-xs sm:text-sm text-start">{localizedChildLabel}</span>
                          </div>

                          {/* Hover glow effect for children */}
                          {!isChildActiveState && (
                            <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-[hsl(var(--teal-vivid))]/10 to-[hsl(var(--green-bright))]/10 rounded-lg" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>


      {/* User Profile / Logout Section */}
      {isUserAuthenticated && (
        compact ? (
          /* When collapsed to icon only: Show language switcher, theme toggle & profile photo for student portal, otherwise logout button */
          <div className="p-3 border-t border-[hsl(var(--card-border))] flex flex-col items-center justify-center gap-2 shrink-0">
            <LanguageSwitcher compact={true} />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5" />}
            </button>
            {isStudentPortal ? (
              <button
                onClick={() => {
                  navigate('/student/profile');
                  if (onNavigate) onNavigate();
                }}
                className={cn(
                  "relative rounded-full transition-all duration-200 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--teal-vivid))] cursor-pointer",
                  location.pathname.includes('/student/profile') && "ring-2 ring-[hsl(var(--teal-vivid))] ring-offset-2 ring-offset-[hsl(var(--sidebar-bg))]"
                )}
                title={userProfile?.fullName ? `${userProfile.fullName} - View Profile` : "View Profile"}
                aria-label="View Profile"
              >
                {renderProfileAvatar('compact')}
              </button>
            ) : (
              <button
                onClick={onLogout}
                className="p-2.5 rounded-xl text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div>
        ) : (
          /* When expanded: Full User Profile + Language Switcher + Theme Toggle + Logout */
          userProfile ? (
            <div className="p-3 border-t border-[hsl(var(--card-border))] transition-all duration-200">
              <div className="flex items-center justify-between gap-1.5 px-1 py-1 bg-[hsl(var(--sidebar-hover))]/40 rounded-2xl border border-[hsl(var(--card-border))] shadow-sm">
                <button
                  onClick={() => {
                    const portal = getPortalType(location.pathname || '');
                    const userData = getUserData(portal);
                    const userType = userData?.user_type || portal;
                    if (userType === 'student') {
                      navigate('/student/profile');
                    } else if (userType === 'teacher') {
                      navigate('/teacher/profile');
                    } else if (userType === 'admin') {
                      navigate('/admin/profile');
                    }
                    if (onNavigate) onNavigate();
                  }}
                  className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity cursor-pointer text-start flex-1"
                  title="View Profile"
                >
                  {renderProfileAvatar('normal')}
                  <div className="min-w-0 flex-1 flex flex-col items-start text-start">
                    <p className="text-xs font-bold text-foreground truncate capitalize w-full text-start leading-tight">{userProfile.fullName}</p>
                    {(userProfile.planName || (!userProfile.isCustomCurriculum && userProfile.grade)) && (
                      <p className="text-[10px] font-bold text-[#27C87C] truncate tracking-wider leading-tight mb-0.5" style={{ textShadow: '0 0 10px rgba(39, 200, 124, 0.2)' }}>
                        <span className="capitalize">{userProfile.planName?.toLowerCase()}</span> {!userProfile.isCustomCurriculum && userProfile.grade && <span className="mx-1.5 opacity-80">-</span>} {!userProfile.isCustomCurriculum && userProfile.grade && <span className="capitalize">{userProfile.grade?.toLowerCase()}</span>}
                      </p>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-0.5 shrink-0">
                  {/* Language Switcher */}
                  <LanguageSwitcher compact={true} />
                  {/* Theme Toggle Button */}
                  <button
                    onClick={toggleTheme}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
                    title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                    aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                  >
                    {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
                  </button>
                  {/* Logout Icon Button */}
                  <button
                    onClick={onLogout}
                    className="p-1.5 rounded-lg transition-all duration-300 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    title="Logout"
                    aria-label="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-3 border-t border-[hsl(var(--card-border))] transition-all duration-200">
              <div className="flex items-center justify-end gap-1.5 px-1 py-1 bg-[hsl(var(--sidebar-hover))]/40 rounded-2xl border border-[hsl(var(--card-border))] shadow-sm">
                <LanguageSwitcher compact={true} />
                <button
                  onClick={toggleTheme}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
                  title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                  aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                >
                  {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
                </button>
                <button
                  onClick={onLogout}
                  className="p-1.5 rounded-lg transition-all duration-300 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  title="Logout"
                  aria-label="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          )
        )
      )}

      {/* Guest CTA: Unlock Full Access */}
      {!isUserAuthenticated && (
        <div className={cn('border-t border-[hsl(var(--card-border))]', compact ? 'p-2 flex flex-col items-center gap-2' : 'p-4')}>
          {compact ? (
            <>
              <LanguageSwitcher compact={true} />
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={onUnlockFullAccess}
                className="w-12 h-12 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white flex items-center justify-center shadow-sm hover:opacity-95 transition-opacity"
                aria-label={t('nav:unlockAccess', 'Login / Signup')}
                title={t('nav:unlockAccess', 'Login / Signup')}
              >
                <CreditCard className="w-5 h-5" />
              </button>
            </>
          ) : (
            <div className="rounded-xl bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center" aria-hidden="true">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11V7a4 4 0 10-8 0v4m0 0H4a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2v-6a2 2 0 00-2-2H4z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground">{t('nav:unlockAccess', 'Unlock Full Access')}</p>
                    <div className="flex items-center gap-1">
                      <LanguageSwitcher compact={true} />
                      <button
                        onClick={toggleTheme}
                        className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
                        title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                      >
                        {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={onUnlockFullAccess}
                className="mt-3 w-full h-9 rounded-lg bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white font-semibold text-sm hover:opacity-95 transition-opacity"
                aria-label={t('nav:unlockAccess', 'Login / Signup')}
              >
                {t('nav:unlockAccess', 'Login / Signup')}
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
